<template>
  <div v-if="isAdmin" class="av-chatbot-root">
    <button
      v-if="!open"
      type="button"
      class="av-chatbot-fab"
      :aria-label="'Open ' + title"
      :title="title"
      @click="openPanel"
    >
      <img :src="iconIdle" class="av-chatbot-fab-icon" :alt="title" />
    </button>

    <div
      v-if="open"
      class="av-chatbot-panel"
      :style="panelStyle"
      role="dialog"
      :aria-label="title"
    >
      <div
        class="av-chatbot-resize"
        title="Resize"
        aria-hidden="true"
        @mousedown.prevent="startResize"
      />

      <!-- List view -->
      <div v-if="!activeId" class="av-chatbot-view">
        <div class="av-chatbot-header" @mousedown="startDrag">
          <img :src="iconIdle" class="av-chatbot-header-icon" :alt="title" />
          <div class="av-chatbot-header-text">
            <div class="av-chatbot-header-title">{{ title }}</div>
            <div class="av-chatbot-header-sub">{{ subtitle }}</div>
          </div>
          <button
            v-if="conversations.length"
            type="button"
            class="av-chatbot-icon-btn"
            title="Delete all chats"
            @click.stop="deleteAllChats"
          >
            <v-icon small>mdi-broom</v-icon>
          </button>
          <button
            type="button"
            class="av-chatbot-icon-btn"
            title="Close"
            @click.stop="closePanel"
          >
            <v-icon small>mdi-close</v-icon>
          </button>
        </div>

        <div class="av-chatbot-body">
          <div v-if="listLoading" class="av-chatbot-muted pa-3">Loading…</div>
          <div v-else-if="!conversations.length" class="av-chatbot-muted pa-3">
            No chats yet. Start a new conversation.
          </div>
          <div v-else>
            <div v-for="group in groupedConversations" :key="group.label" class="av-chatbot-group">
              <div class="av-chatbot-group-label">{{ group.label }}</div>
              <div
                v-for="item in group.items"
                :key="item.id"
                class="av-chatbot-list-item"
              >
                <button
                  v-if="renamingId !== item.id"
                  type="button"
                  class="av-chatbot-list-main"
                  @click="openConversation(item.id)"
                >
                  {{ item.title || 'New chat' }}
                </button>
                <div v-else class="av-chatbot-rename-row">
                  <input
                    v-model="renameDraft"
                    class="av-chatbot-rename-input"
                    @keydown.enter.prevent="saveRename(item)"
                    @keydown.esc.prevent="cancelRename"
                  />
                  <button type="button" class="av-chatbot-icon-btn" title="Cancel" @click="cancelRename">
                    <v-icon small>mdi-close</v-icon>
                  </button>
                  <button type="button" class="av-chatbot-icon-btn" title="Save" @click="saveRename(item)">
                    <v-icon small>mdi-check</v-icon>
                  </button>
                </div>
                <v-menu v-if="renamingId !== item.id" offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      type="button"
                      class="av-chatbot-icon-btn"
                      v-bind="attrs"
                      v-on="on"
                      title="More"
                      @click.stop
                    >
                      <v-icon small>mdi-dots-vertical</v-icon>
                    </button>
                  </template>
                  <v-list dense>
                    <v-list-item @click="beginRename(item)">
                      <v-list-item-title>Rename</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="deleteConversation(item.id)">
                      <v-list-item-title>Delete</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
            <button
              v-if="hasMore"
              type="button"
              class="av-chatbot-load-more"
              :disabled="listLoading"
              @click="loadMore"
            >
              Load older chats
            </button>
          </div>
          <button type="button" class="av-chatbot-new" title="New chat" @click="createChat">
            <v-icon>mdi-plus</v-icon>
          </button>
        </div>
      </div>

      <!-- Chat view -->
      <div v-else class="av-chatbot-view">
        <div class="av-chatbot-header" @mousedown="startDrag">
          <button type="button" class="av-chatbot-header-nav" @click.stop="returnToList">
            <img :src="waiting ? iconChatting : iconIdle" class="av-chatbot-header-icon" :alt="title" />
            <div class="av-chatbot-header-text">
              <div class="av-chatbot-header-title">{{ activeTitle }}</div>
              <div class="av-chatbot-header-sub">{{ subtitle }}</div>
            </div>
          </button>
          <button type="button" class="av-chatbot-icon-btn" title="Return" @click.stop="returnToList">
            <v-icon small>mdi-keyboard-return</v-icon>
          </button>
          <button type="button" class="av-chatbot-icon-btn" title="Close" @click.stop="closePanel">
            <v-icon small>mdi-close</v-icon>
          </button>
        </div>

        <div ref="messagesEl" class="av-chatbot-messages" @scroll="onMessagesScroll">
          <div v-if="!visibleMessages.length && !waiting" class="av-chatbot-muted pa-3">
            Ask about counts, recent activity, or KPI snapshots.
          </div>
          <div
            v-for="message in visibleMessages"
            :key="message.id"
            class="av-chatbot-message"
            :class="message.role === 'user' ? 'is-user' : 'is-assistant'"
          >
            <div class="av-chatbot-message-role">{{ message.role === 'user' ? 'You' : 'Assistant' }}</div>
            <div class="av-chatbot-message-body">{{ message.content }}</div>
          </div>
          <div v-if="waiting" class="av-chatbot-message is-assistant">
            <div class="av-chatbot-message-role">Assistant</div>
            <div class="av-chatbot-loading"><span /><span /><span /></div>
          </div>
          <div v-if="error" class="av-chatbot-error">{{ error }}</div>
        </div>

        <button
          v-if="showScrollButton"
          type="button"
          class="av-chatbot-scroll-btn"
          title="Scroll to bottom"
          @click="scrollToBottom(true)"
        >
          <v-icon small>mdi-arrow-down</v-icon>
        </button>

        <div class="av-chatbot-footer">
          <textarea
            ref="inputEl"
            v-model="draft"
            class="av-chatbot-input"
            rows="1"
            placeholder="Write a message..."
            :disabled="waiting"
            @keydown="onInputKeydown"
            @input="autoGrow"
          />
          <button
            type="button"
            class="av-chatbot-send"
            :disabled="waiting || !draft.trim()"
            :aria-label="waiting ? 'Waiting' : 'Send message'"
            @click="sendMessage"
          >
            <v-icon small>mdi-send</v-icon>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import iconIdle from '@/assets/images/chatbot/icon-idle.png'
import iconChatting from '@/assets/images/chatbot/icon-chatting.png'

const SIZE_KEY = 'allviewChatbotPanelSize'
const OFFSET_KEY = 'allviewChatbotPanelOffset'
const MIN_W = 320
const MIN_H = 288
const MARGIN = 20

export default {
  name: 'BackofficeChatbot',
  data () {
    return {
      iconIdle,
      iconChatting,
      title: 'AllView Assistant',
      subtitle: 'Backoffice chatbot',
      isAdmin: false,
      open: false,
      conversations: [],
      total: 0,
      listLimit: 50,
      listLoading: false,
      activeId: null,
      activeTitle: 'New chat',
      messages: [],
      draft: '',
      waiting: false,
      error: '',
      renamingId: null,
      renameDraft: '',
      showScrollButton: false,
      size: { width: 420, height: 560 },
      offset: { right: MARGIN, bottom: MARGIN },
      drag: null,
      resize: null
    }
  },
  computed: {
    panelStyle () {
      return {
        width: this.size.width + 'px',
        height: this.size.height + 'px',
        right: this.offset.right + 'px',
        bottom: this.offset.bottom + 'px'
      }
    },
    hasMore () {
      return this.conversations.length < this.total
    },
    visibleMessages () {
      return (this.messages || []).filter((m) => m.role === 'user' || m.role === 'assistant')
    },
    groupedConversations () {
      const groups = { Today: [], Yesterday: [], Previous: [] }
      const now = new Date()
      const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const startYesterday = new Date(startToday)
      startYesterday.setDate(startYesterday.getDate() - 1)
      for (const item of this.conversations) {
        const when = new Date(item.updated_at || item.created_at)
        if (when >= startToday) groups.Today.push(item)
        else if (when >= startYesterday) groups.Yesterday.push(item)
        else groups.Previous.push(item)
      }
      return ['Today', 'Yesterday', 'Previous']
        .map((label) => ({ label, items: groups[label] }))
        .filter((g) => g.items.length)
    }
  },
  async mounted () {
    this.restoreGeometry()
    window.addEventListener('resize', this.clampGeometry)
    try {
      const role = await this.api('backoffice/role')
      const roleId = Array.isArray(role) ? (role[0] && role[0].role_id) : (role && role.role_id)
      this.isAdmin = Number(roleId) === 7
      if (this.isAdmin) await this.refreshList()
    } catch (error) {
      // Layout is backoffice-only; still show FAB and let API enforce role 7.
      this.isAdmin = true
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.clampGeometry)
    this.stopPointerListeners()
  },
  methods: {
    async api (path, method, body) {
      const options = {
        method: method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Auth-Token': this.$cookie.get('jwt_token')
        }
      }
      if (method === 'POST' || method === 'PATCH' || method === 'DELETE') {
        if (body !== undefined) options.body = JSON.stringify(body || {})
      }
      let response = await fetch(process.env.VUE_APP_API_ENDPOINT + path, options)
      response = await response.json()
      if (!response.success) throw new Error(response.message || 'Error')
      return response.result
    },
    restoreGeometry () {
      try {
        const size = JSON.parse(localStorage.getItem(SIZE_KEY) || 'null')
        if (size && size.width && size.height) this.size = size
        const offset = JSON.parse(localStorage.getItem(OFFSET_KEY) || 'null')
        if (offset && Number.isFinite(offset.right) && Number.isFinite(offset.bottom)) {
          this.offset = offset
        }
      } catch (error) { /* ignore */ }
      this.clampGeometry()
    },
    persistGeometry () {
      localStorage.setItem(SIZE_KEY, JSON.stringify(this.size))
      localStorage.setItem(OFFSET_KEY, JSON.stringify(this.offset))
    },
    clampGeometry () {
      const maxW = Math.min(window.innerWidth * 0.9, window.innerWidth - this.offset.right - MARGIN)
      const maxH = Math.max(MIN_H, window.innerHeight - 64 - 2 * MARGIN)
      this.size = {
        width: Math.max(MIN_W, Math.min(this.size.width, maxW)),
        height: Math.max(MIN_H, Math.min(this.size.height, maxH))
      }
      this.offset = {
        right: Math.max(MARGIN, Math.min(this.offset.right, window.innerWidth - this.size.width - MARGIN)),
        bottom: Math.max(MARGIN, Math.min(this.offset.bottom, window.innerHeight - this.size.height - MARGIN))
      }
    },
    openPanel () {
      this.open = true
      this.clampGeometry()
      if (!this.conversations.length) this.refreshList()
    },
    closePanel () {
      this.open = false
      this.activeId = null
      this.error = ''
    },
    returnToList () {
      this.activeId = null
      this.messages = []
      this.error = ''
      this.refreshList()
    },
    async refreshList () {
      this.listLoading = true
      try {
        const result = await this.api(
          'backoffice/chatbot/conversations?limit=' + this.listLimit + '&offset=0'
        )
        this.conversations = result.items || []
        this.total = result.total || 0
      } catch (error) {
        this.error = error.message || 'Failed to load chats'
      } finally {
        this.listLoading = false
      }
    },
    async loadMore () {
      this.listLoading = true
      try {
        const result = await this.api(
          'backoffice/chatbot/conversations?limit=' + this.listLimit +
          '&offset=' + this.conversations.length
        )
        this.conversations = this.conversations.concat(result.items || [])
        this.total = result.total || this.total
      } catch (error) {
        this.error = error.message || 'Failed to load chats'
      } finally {
        this.listLoading = false
      }
    },
    async createChat () {
      try {
        const created = await this.api('backoffice/chatbot/conversations', 'POST', { title: 'New chat' })
        this.conversations = [created].concat(this.conversations)
        this.total += 1
        await this.openConversation(created.id)
      } catch (error) {
        this.error = error.message || 'Failed to create chat'
      }
    },
    async openConversation (id) {
      this.error = ''
      try {
        const result = await this.api('backoffice/chatbot/conversations/' + id)
        this.activeId = result.conversation.id
        this.activeTitle = result.conversation.title || 'New chat'
        this.messages = result.messages || []
        this.$nextTick(() => this.scrollToBottom(true))
      } catch (error) {
        this.error = error.message || 'Conversation not found'
      }
    },
    beginRename (item) {
      this.renamingId = item.id
      this.renameDraft = item.title || ''
    },
    cancelRename () {
      this.renamingId = null
      this.renameDraft = ''
    },
    async saveRename (item) {
      try {
        const updated = await this.api(
          'backoffice/chatbot/conversations/' + item.id,
          'PATCH',
          { title: this.renameDraft }
        )
        item.title = updated.title
        this.cancelRename()
      } catch (error) {
        this.error = error.message || 'Rename failed'
      }
    },
    async deleteConversation (id) {
      try {
        await this.api('backoffice/chatbot/conversations/' + id, 'DELETE')
        this.conversations = this.conversations.filter((c) => c.id !== id)
        this.total = Math.max(0, this.total - 1)
        if (this.activeId === id) this.returnToList()
      } catch (error) {
        this.error = error.message || 'Delete failed'
      }
    },
    async deleteAllChats () {
      const ids = this.conversations.map((c) => c.id)
      for (const id of ids) {
        try {
          await this.api('backoffice/chatbot/conversations/' + id, 'DELETE')
        } catch (error) { /* continue */ }
      }
      this.conversations = []
      this.total = 0
      this.activeId = null
    },
    onInputKeydown (event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.sendMessage()
      }
    },
    autoGrow () {
      const el = this.$refs.inputEl
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(120, el.scrollHeight) + 'px'
    },
    async sendMessage () {
      const content = (this.draft || '').trim()
      if (!content || this.waiting || !this.activeId) return
      this.draft = ''
      this.$nextTick(() => this.autoGrow())
      this.waiting = true
      this.error = ''
      this.messages = this.messages.concat([{
        id: 'tmp-user-' + Date.now(),
        role: 'user',
        content
      }])
      this.$nextTick(() => this.scrollToBottom(true))
      try {
        const result = await this.api(
          'backoffice/chatbot/conversations/' + this.activeId + '/messages',
          'POST',
          { content }
        )
        this.messages = result.messages || []
        this.activeTitle = (result.conversation && result.conversation.title) || this.activeTitle
        await this.refreshList()
      } catch (error) {
        this.error = error.message || 'Model unavailable'
      } finally {
        this.waiting = false
        this.$nextTick(() => this.scrollToBottom(true))
      }
    },
    onMessagesScroll () {
      const el = this.$refs.messagesEl
      if (!el) return
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight
      this.showScrollButton = distance > 80
    },
    scrollToBottom (force) {
      const el = this.$refs.messagesEl
      if (!el) return
      if (!force && this.showScrollButton) return
      el.scrollTop = el.scrollHeight
      this.showScrollButton = false
    },
    startDrag (event) {
      if (event.target.closest('button') || event.target.closest('a')) return
      this.drag = {
        startX: event.clientX,
        startY: event.clientY,
        right: this.offset.right,
        bottom: this.offset.bottom
      }
      window.addEventListener('mousemove', this.onDragMove)
      window.addEventListener('mouseup', this.stopPointerListeners)
    },
    onDragMove (event) {
      if (!this.drag) return
      const dx = event.clientX - this.drag.startX
      const dy = event.clientY - this.drag.startY
      this.offset = {
        right: this.drag.right - dx,
        bottom: this.drag.bottom - dy
      }
      this.clampGeometry()
    },
    startResize (event) {
      this.resize = {
        startX: event.clientX,
        startY: event.clientY,
        width: this.size.width,
        height: this.size.height
      }
      window.addEventListener('mousemove', this.onResizeMove)
      window.addEventListener('mouseup', this.stopPointerListeners)
    },
    onResizeMove (event) {
      if (!this.resize) return
      const dx = event.clientX - this.resize.startX
      const dy = event.clientY - this.resize.startY
      this.size = {
        width: this.resize.width - dx,
        height: this.resize.height - dy
      }
      this.clampGeometry()
    },
    stopPointerListeners () {
      window.removeEventListener('mousemove', this.onDragMove)
      window.removeEventListener('mousemove', this.onResizeMove)
      window.removeEventListener('mouseup', this.stopPointerListeners)
      if (this.drag || this.resize) this.persistGeometry()
      this.drag = null
      this.resize = null
    }
  }
}
</script>

<style scoped>
.av-chatbot-root {
  position: fixed;
  z-index: 10050;
  pointer-events: none;
}
.av-chatbot-fab,
.av-chatbot-panel {
  pointer-events: auto;
}
.av-chatbot-fab {
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 58px;
  height: 58px;
  border: 0;
  border-radius: 50%;
  padding: 0;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  display: grid;
  place-items: center;
}
.av-chatbot-fab-icon {
  width: 56px;
  height: 56px;
  object-fit: contain;
  pointer-events: none;
  filter: drop-shadow(0 8px 18px rgba(15, 35, 52, 0.22));
}
.av-chatbot-panel {
  position: fixed;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(15, 35, 52, 0.22);
  overflow: hidden;
  min-width: 320px;
  min-height: 288px;
}
.av-chatbot-resize {
  position: absolute;
  top: 0;
  left: 0;
  width: 16px;
  height: 16px;
  cursor: nwse-resize;
  z-index: 2;
}
.av-chatbot-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.av-chatbot-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e6e8eb;
  background: #f7f8fa;
  cursor: move;
  user-select: none;
}
.av-chatbot-header-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  flex: 1;
  min-width: 0;
  text-align: left;
}
.av-chatbot-header-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
  flex: 0 0 auto;
}
.av-chatbot-header-text {
  min-width: 0;
  flex: 1;
}
.av-chatbot-header-title {
  font-weight: 600;
  font-size: 14px;
  color: #1f2a37;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.av-chatbot-header-sub {
  font-size: 11px;
  color: #6b7280;
}
.av-chatbot-icon-btn {
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
}
.av-chatbot-body {
  position: relative;
  flex: 1;
  overflow: auto;
  min-height: 0;
  padding-bottom: 56px;
}
.av-chatbot-group {
  padding: 8px 12px 0;
}
.av-chatbot-group-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  margin-bottom: 4px;
}
.av-chatbot-list-item {
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  padding: 2px 4px;
}
.av-chatbot-list-item:hover {
  background: #f3f4f6;
}
.av-chatbot-list-main {
  flex: 1;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 8px 6px;
  cursor: pointer;
  color: #374151;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.av-chatbot-rename-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
}
.av-chatbot-rename-input {
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 13px;
}
.av-chatbot-new {
  position: absolute;
  right: 16px;
  bottom: 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0;
  background: #1976d2;
  color: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(25, 118, 210, 0.35);
}
.av-chatbot-load-more {
  display: block;
  width: calc(100% - 24px);
  margin: 8px 12px 16px;
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  font-size: 12px;
}
.av-chatbot-messages {
  flex: 1;
  overflow: auto;
  min-height: 0;
  padding: 12px;
}
.av-chatbot-message {
  max-width: 92%;
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.4;
}
.av-chatbot-message.is-user {
  margin-left: auto;
  background: #e3f2fd;
  border: 1px solid #90caf9;
}
.av-chatbot-message.is-assistant {
  margin-right: auto;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}
.av-chatbot-message-role {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  margin-bottom: 2px;
}
.av-chatbot-message-body {
  white-space: pre-wrap;
  word-break: break-word;
  color: #111827;
}
.av-chatbot-loading span {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 4px;
  border-radius: 50%;
  background: #9ca3af;
  animation: av-chatbot-bounce 1s infinite ease-in-out;
}
.av-chatbot-loading span:nth-child(2) { animation-delay: 0.15s; }
.av-chatbot-loading span:nth-child(3) { animation-delay: 0.3s; }
@keyframes av-chatbot-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
}
.av-chatbot-error {
  color: #b91c1c;
  font-size: 12px;
  padding: 4px 2px 8px;
}
.av-chatbot-scroll-btn {
  position: absolute;
  right: 18px;
  bottom: 78px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
}
.av-chatbot-footer {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding: 10px 12px;
  border-top: 1px solid #e6e8eb;
  background: #fff;
}
.av-chatbot-input {
  flex: 1;
  resize: none;
  min-height: 38px;
  max-height: 120px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
}
.av-chatbot-send {
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 8px;
  background: #1976d2;
  color: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
}
.av-chatbot-send:disabled {
  opacity: 0.5;
  cursor: default;
}
.av-chatbot-muted {
  color: #6b7280;
  font-size: 13px;
}
</style>
